// Helpers to convert between UTC ISO strings (stored in DB) and
// the naive "YYYY-MM-DDTHH:MM" format used by <input type="datetime-local">,
// interpreting the naive value as *Europe/Madrid* local time (with DST awareness).

const MADRID_TZ = 'Europe/Madrid';

const madridParts = (date) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: MADRID_TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const map = Object.fromEntries(
    parts.filter((p) => p.type !== 'literal').map((p) => [p.type, p.value])
  );
  return `${map.year}-${map.month}-${map.day}T${map.hour}:${map.minute}`;
};

/**
 * Convert an ISO UTC string ("2026-10-01T18:30:00Z") to a datetime-local
 * value in Europe/Madrid ("2026-10-01T20:30"). Returns "" for empty input.
 */
export const isoUtcToMadridLocal = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return madridParts(d);
  } catch {
    return '';
  }
};

/**
 * Convert a datetime-local value interpreted as Europe/Madrid time
 * ("2026-10-01T20:30") to an ISO UTC string ("2026-10-01T18:30:00.000Z").
 * Returns "" for empty input. Handles DST correctly by iterating twice.
 */
export const madridLocalToIsoUtc = (local) => {
  if (!local) return '';
  // Ensure "YYYY-MM-DDTHH:MM"
  const clean = local.length >= 16 ? local.slice(0, 16) : local;
  // First guess: treat local as if it were UTC
  let utc = new Date(clean + ':00Z');
  if (Number.isNaN(utc.getTime())) return '';
  for (let i = 0; i < 3; i++) {
    const madridStr = madridParts(utc);
    if (madridStr === clean) break;
    const diffMs =
      new Date(madridStr + ':00Z').getTime() -
      new Date(clean + ':00Z').getTime();
    utc = new Date(utc.getTime() - diffMs);
  }
  return utc.toISOString();
};

/**
 * Human-readable Madrid preview for a UTC ISO string.
 * "Jueves, 1 de octubre · 18:30" (or "" if empty).
 */
export const previewMadrid = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const date = d.toLocaleDateString('es-ES', {
      weekday: 'long', day: 'numeric', month: 'long',
      timeZone: MADRID_TZ,
    });
    const time = d.toLocaleTimeString('es-ES', {
      hour: '2-digit', minute: '2-digit',
      timeZone: MADRID_TZ,
    });
    return `${date} · ${time} (hora de Madrid)`;
  } catch {
    return '';
  }
};
